import React from 'react';
import { Helmet } from 'react-helmet-async';
import { COMPANY_INFO } from '../data/company';
import ContactFormSection from '../components/ContactFormSection';

export default function ContactPage() {
  return (
    <>
      <Helmet>
        <title>Kontakt - OP Rengøringsservice</title>
        <meta name="description" content="Kontakt OP Rengøringsservice i Nibe på tlf. (+45) 98 35 15 47 eller mail info@opservice.dk. Få et uforpligtende tilbud på rengøring, vinduespolering og haveservice." />
        <meta property="og:title" content="Kontakt - OP Rengøringsservice" />
        <meta property="og:description" content="Skalhuse 13, 9240 Nibe. Tlf: (+45) 98 35 15 47. E-mail: info@opservice.dk" />
        <link rel="canonical" href="https://opservice.dk/kontakt" />
      </Helmet>

      {/* Header Banner */}
      <section className="bg-slate-900 text-white py-16 lg:py-20 relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 text-center max-w-3xl">
          <span className="text-xs font-bold text-emerald-400 uppercase tracking-widest bg-emerald-500/10 px-3 py-1 rounded-full border border-emerald-500/20 inline-block mb-3">
            Kontakt Os
          </span>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight text-white mb-4">
            Kontakt OP Rengøringsservice
          </h1>
          <p className="text-slate-300 text-base sm:text-lg leading-relaxed">
            Vi står altid klar til at besvare dine spørgsmål eller give et uforpligtende tilbud på din opgave.
          </p>
        </div>
      </section>

      {/* Company Info Cards Section */}
      <section className="py-12 bg-slate-50 border-b border-slate-200/80">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            
            {/* Address Card */}
            <div className="bg-white p-8 rounded-2xl border border-slate-200 shadow-xs flex flex-col items-center text-center">
              <div className="w-14 h-14 rounded-2xl bg-sky-100 text-sky-800 flex items-center justify-center text-2xl mb-4 shadow-inner">
                <i className="fa-solid fa-location-dot" aria-hidden="true" />
              </div>
              <h3 className="font-bold text-slate-900 text-lg mb-1">Adresse</h3>
              <p className="text-slate-600 text-sm font-medium">{COMPANY_INFO.address}</p>
              <span className="text-xs text-slate-500 mt-2">Nibe og omegn</span>
            </div>

            {/* Phone Card */}
            <div className="bg-white p-8 rounded-2xl border border-slate-200 shadow-xs flex flex-col items-center text-center">
              <div className="w-14 h-14 rounded-2xl bg-emerald-100 text-emerald-800 flex items-center justify-center text-2xl mb-4 shadow-inner">
                <i className="fa-solid fa-phone" aria-hidden="true" />
              </div>
              <h3 className="font-bold text-slate-900 text-lg mb-1">Telefon</h3>
              <a
                href={`tel:${COMPANY_INFO.phoneRaw}`}
                className="text-slate-900 hover:text-emerald-600 font-extrabold text-base transition-colors whitespace-nowrap-custom"
              >
                {COMPANY_INFO.phone}
              </a>
              <span className="text-xs text-slate-500 mt-2">Ring for hurtig betjening</span>
            </div>

            {/* Email & Facebook Card */}
            <div className="bg-white p-8 rounded-2xl border border-slate-200 shadow-xs flex flex-col items-center text-center">
              <div className="w-14 h-14 rounded-2xl bg-sky-100 text-sky-800 flex items-center justify-center text-2xl mb-4 shadow-inner">
                <i className="fa-solid fa-envelope" aria-hidden="true" />
              </div>
              <h3 className="font-bold text-slate-900 text-lg mb-1">E-mail & Social</h3>
              <a
                href={`mailto:${COMPANY_INFO.email}`}
                className="text-slate-900 hover:text-sky-800 font-bold text-sm transition-colors whitespace-nowrap-custom mb-1"
              >
                {COMPANY_INFO.email}
              </a>
              <a
                href={COMPANY_INFO.facebook}
                target="_blank"
                rel="noopener noreferrer"
                className="text-xs font-semibold text-sky-800 hover:underline inline-flex items-center gap-1.5 mt-1"
              >
                <i className="fa-brands fa-facebook" aria-hidden="true" />
                <span>Besøg os på Facebook</span>
              </a>
            </div>

          </div>
        </div>
      </section>

      {/* Main Webform Section */}
      <ContactFormSection />

      {/* Map visual section */}
      <section className="py-16 bg-white border-t border-slate-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-slate-900 rounded-3xl p-8 sm:p-12 text-white shadow-xl grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
            <div className="lg:col-span-6 space-y-4">
              <span className="text-xs font-bold text-emerald-400 uppercase tracking-widest bg-emerald-500/10 px-3 py-1 rounded-full border border-emerald-500/20 inline-block">
                Hvor finder du os?
              </span>
              <h2 className="text-2xl sm:text-3xl font-extrabold text-white">
                Skalhuse 13, 9240 Nibe
              </h2>
              <p className="text-slate-300 text-sm leading-relaxed">
                Vores kontor og servicestation er placeret i Nibe. Herfra betjener vores 20+ medarbejdere både private hjem, virksomheder og boligforeninger i hele området.
              </p>
              <div className="pt-2">
                <a
                  href={`https://maps.google.com/?q=${encodeURIComponent(COMPANY_INFO.address)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 bg-emerald-600 hover:bg-emerald-500 text-white font-bold py-3 px-5 rounded-xl transition-colors text-sm shadow"
                >
                  <i className="fa-solid fa-map-location-dot" aria-hidden="true" />
                  <span>Åbn i Google Maps</span>
                </a>
              </div>
            </div>

            <div className="lg:col-span-6">
              <div className="rounded-2xl overflow-hidden border border-slate-700 shadow-xl aspect-video w-full bg-slate-800">
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d1072698.5021550062!2d8.49440619665677!3d56.80468641451183!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x464925be9433a175%3A0x300afc9aca85420!2sRegion%20Nordjylland!5e0!3m2!1sda!2sdk!4v1785328496586!5m2!1sda!2sdk"
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  allowFullScreen=""
                  loading="lazy"
                  referrerPolicy="strict-origin-when-cross-origin"
                  title="OP Rengøringsservice Nibe Kort"
                  className="w-full h-full border-0"
                />
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
